import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useRouter } from 'next/router';
import TrustedOneHundredInstitutions from 'components/TrustedOneHundredInstitutions';
import { useStyles } from './styles';
import { Box, Container, TextField, Typography, CircularProgress } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CustomButton from 'components/CustomButton';
import { getCountries, getCountryCode } from 'utils/countries';
import ContactService from 'services/ContactService';
import { isEmail } from 'class-validator';
import PaymentService from 'services/PaymentService';
import WhatsappPhoneTextField from 'components/WhatsappPhoneTextField';
import getCleverTap from 'utils/clevertap';
import { useAuthUser } from 'features/auth/AuthUserContext';

const organizationTypes = [
  'Pharmaceutical',
  'Biotech',
  'University',
  'Society',
  'Journal',
  'Other',
];

export default function MainContent() {
  const classes = useStyles();
  const { user } = useAuthUser();
  const router = useRouter();
  const [errorFields, setErrorFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [userCountry, setUserCountry] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    organizationName: '',
    organizationType: '',
    organizationCountry: '',
    additionalInfo: '',
  });
  const clevertap = getCleverTap();

  useEffect(() => {
    async function fetchCountry() {
      try {
        const paymentService = new PaymentService();
        const { data } = await paymentService.data();
        const userCountry = data?.country;
        const userCountryCode = data?.countryCode;

        if (userCountry && userCountryCode) {
          setUserCountry(userCountry);
          setCountryCode(userCountryCode);
        }
      } catch (error) {
        console.error('Error fetching country name:', error);
      }
    }
    fetchCountry();
  }, []);

  function sendTrackingEvents() {
    ReactGA.event({
      category: 'institutions',
      action: 'institution_form_sent',
      label: 'When the user clicks on the "Submit" button to submit the form',
      value: user?.id,
    });

    clevertap.onUserLogin.push({
      Site: {
        Name: formData.email.split('@')[0],
        Phone: `+ ${whatsappNumber}`,
        Email: formData.email,
        Location: formData.organizationCountry,
        'MSG-email': true,
        'MSG-push': true,
        'Institution Lead MTG': 'Yes',
      },
    });

    clevertap.event.push('Subscription scheduled', {
      Product: 'MTG',
      Email: formData.email,
      URL: router.asPath,
      Description: 'Institution Form Sent',
      Locale: 'Institutions Page',
    });
  }

  function getErrorFields() {
    const fields = [];

    if (!formData.name) {
      fields.push('name');
    }

    if (!isEmail(formData.email)) {
      fields.push('email');
    }

    if (whatsappNumber.length <= 8) {
      fields.push('whatsappNumber');
    }

    if (!formData.role) {
      fields.push('role');
    }

    if (!formData.organizationName) {
      fields.push('organizationName');
    }

    if (!formData.organizationType) {
      fields.push('organizationType');
    }

    if (!formData.organizationCountry) {
      fields.push('organizationCountry');
    }

    return fields;
  }

  function canSubmit() {
    const errors = getErrorFields();

    if (errors.length > 0) {
      setErrorFields(errors);
      return false;
    }

    return true;
  }

  function handleFieldChange(field, value) {
    if (field === 'whatsappNumber') {
      setWhatsappNumber(value);
    }

    if (field === 'userCountry') {
      setUserCountry(value);
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [field]: value,
      }));
    }

    if (!!value && errorFields.includes(field)) {
      setErrorFields((prevErrorFields) =>
        prevErrorFields.filter((errorField) => errorField !== field),
      );
    }
  }

  useEffect(() => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      organizationCountry: getCountryCode(userCountry),
    }));
  }, [userCountry]);

  function handleChange(event) {
    const { name, value } = event.target;

    handleFieldChange(name, value);
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (canSubmit()) {
      setIsLoading(true);
      sendTrackingEvents();

      await ContactService.sendContactRequestTrialDemo(formData);

      router.push('/institutions/checkout');
    }
  }

  return (
    <>
      <Box bgcolor="#EEEEEE" py={6}>
        <Container>
          <Typography className={classes.primaryColor} variant="h4" component="h1" align="center">
            <b>Request a Trial or Demo for your Institution</b>
          </Typography>

          <Box display="flex" justifyContent="center" pt={6}>
            <Box className={classes.mainBoxContent} display="flex" justifyContent="center">
              <Box textAlign="flex-start" pr={10}>
                <Typography className={classes.textReasons}>
                  <b>
                    Reasons to improve your institution's <br /> scientific communication with{' '}
                    <br /> Mind the Graph:
                  </b>
                </Typography>

                <Box pl={3}>
                  <ul className={classes.list}>
                    <li>Scientifically-accurate illustrations</li>
                    <li>Share creations with multiple collaborators</li>
                    <li>Access designs from anywhere at any time</li>
                    <li>Unlimited users for institutions</li>
                    <li>And more</li>
                  </ul>
                </Box>
              </Box>

              <Box className={classes.mainForm}>
                <form onSubmit={handleSubmit}>
                  <Typography className={classes.inputTextName}>
                    Your name<span>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    label="What's your name?"
                    variant="outlined"
                    name="name"
                    error={errorFields.includes('name')}
                    helperText={
                      errorFields.includes('name') ? 'Please fill this field correctly' : undefined
                    }
                    value={formData.name}
                    onChange={handleChange}
                  />

                  <Typography className={classes.inputTextName}>
                    Your email<span>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    label="What's your email?"
                    variant="outlined"
                    name="email"
                    error={errorFields.includes('email')}
                    helperText={
                      errorFields.includes('email') ? 'Please fill this field correctly' : undefined
                    }
                    value={formData.email}
                    onChange={handleChange}
                  />

                  <Typography className={classes.inputTextName}>
                    Your Phone<span>*</span>
                  </Typography>
                  <WhatsappPhoneTextField
                    variant="outlined"
                    countryProp={countryCode.toLowerCase()}
                    label="Phone Number"
                    marginRightProp={0}
                    disabled={isLoading}
                    showInformation={false}
                    onChange={(value) => handleFieldChange('whatsappNumber', value)}
                    value={whatsappNumber}
                    error={errorFields.includes('whatsappNumber')}
                    helperText={
                      errorFields.includes('whatsappNumber') ? 'Invalid phone number' : undefined
                    }
                  />

                  <Typography className={classes.inputTextName}>
                    Professional role<span>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    label="What's your role?"
                    variant="outlined"
                    name="role"
                    error={errorFields.includes('role')}
                    helperText={
                      errorFields.includes('role') ? 'Please fill this field correctly' : undefined
                    }
                    value={formData.role}
                    onChange={handleChange}
                  />

                  <Typography className={classes.inputTextName}>
                    Organization name<span>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    label="What's your organization's name?"
                    variant="outlined"
                    name="organizationName"
                    error={errorFields.includes('organizationName')}
                    helperText={
                      errorFields.includes('organizationName')
                        ? 'Please fill this field correctly'
                        : undefined
                    }
                    value={formData.organizationName}
                    onChange={handleChange}
                  />

                  <Typography className={classes.inputTextName}>
                    Organization type<span>*</span>
                  </Typography>
                  <Autocomplete
                    options={organizationTypes}
                    value={formData.organizationType}
                    onChange={(_, value) =>
                      handleChange({ target: { name: 'organizationType', value } })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Organization type"
                        name="organizationType"
                        error={errorFields.includes('organizationType')}
                        helperText={
                          errorFields.includes('organizationType')
                            ? 'Please fill this field correctly'
                            : undefined
                        }
                      />
                    )}
                  />

                  <Typography className={classes.inputTextName}>
                    Country of your organization<span>*</span>
                  </Typography>
                  <Autocomplete
                    options={['', ...getCountries()]}
                    value={userCountry}
                    onChange={(_, value) =>
                      handleChange({ target: { name: 'userCountry', value } })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="What country are you based in?"
                        name="userCountry"
                        error={errorFields.includes('userCountry')}
                        helperText={
                          errorFields.includes('userCountry')
                            ? 'Please fill this field correctly'
                            : undefined
                        }
                      />
                    )}
                  />

                  <Typography className={classes.inputTextName}>
                    Would like to add any other information?
                  </Typography>
                  <TextField
                    fullWidth
                    label="Would like to add any other information?"
                    variant="outlined"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                  />

                  <Box textAlign="end" pt={4}>
                    <CustomButton type="submit">
                      {!isLoading ? (
                        'Request a demo'
                      ) : (
                        <CircularProgress size={24} color="inherit" />
                      )}
                    </CustomButton>
                  </Box>
                </form>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <TrustedOneHundredInstitutions textTitle="Mind the Graph's trusted by 100+ academics institutions worldwide" />
    </>
  );
}
